import { useState } from "react";
import { Loader2, RotateCcw, Shuffle, Sparkles } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { Disclosure } from "@/components/Provenance";
import { fmtElapsed, useElapsed } from "@/lib/hooks";

export const otherEngine = (e) => (e === "flux" ? "nano_banana" : "flux");
export const ENGINE_LABEL = { flux: "FLUX.3 Klein Edit", nano_banana: "Nano Banana 2 Edit" };

export const VersionStrip = ({ versions, currentId, onSelect, isOwner }) => (
  <div className="no-scrollbar -mx-5 flex gap-3 overflow-x-auto px-5 pb-2 sm:mx-0 sm:flex-wrap sm:px-0" data-testid="version-strip">
    {versions.map((v) => {
      const cur = v.id === currentId;
      return (
        <button
          key={v.id}
          disabled={!isOwner || v.status !== "succeeded"}
          onClick={() => onSelect(v)}
          data-testid={`version-item-${v.number}`}
          data-current={cur}
          className={`w-32 shrink-0 text-left transition-opacity duration-300 ${cur ? "" : "opacity-80 hover:opacity-100"}`}
        >
          <div className={`relative aspect-[4/3] overflow-hidden rounded-xl bg-[var(--sand)] ${cur ? "ring-2 ring-[var(--charcoal)] ring-offset-2 ring-offset-[var(--bone)]" : ""}`}>
            {v.outputUrl && <img src={assetUrl(v.outputUrl)} alt={`Version ${v.number}`} className="h-full w-full object-cover" />}
            {v.outputUrl && <Disclosure small testid={`version-disclosure-${v.number}`} />}
            {(v.status === "queued" || v.status === "running") && <div className="absolute inset-0 flex items-center justify-center shimmer"><Loader2 size={18} className="animate-spin text-[var(--charcoal)]" /></div>}
            {v.status === "failed" && <div className="absolute inset-0 flex items-center justify-center bg-[#FDECEA] text-[0.7rem] text-[#B42318]">Didn't render</div>}
            {isOwner && v.status === "rejected" && <div className="absolute inset-x-0 bottom-0 bg-[#B42318]/85 px-1.5 py-0.5 text-[0.62rem] text-white" data-testid={`version-layout-changed-${v.number}`}>Layout changed</div>}
          </div>
          <div className="mt-1.5 text-xs font-medium">V{v.number} · {v.mode === "refine" ? "Refined" : v.mode === "alternative" ? "Alternative" : v.mode === "engine" ? "Other engine" : v.mode === "grounded" ? "Product-grounded" : "Original"}</div>
          <div className="text-[0.68rem] text-[var(--stone-dark)]" data-testid={`version-engine-${v.number}`}>{v.engineLabel}{v.engineReason ? ` · ${v.engineReason}` : ""}</div>
        </button>
      );
    })}
  </div>
);

export const Generating = ({ version }) => {
  const elapsed = useElapsed(version.createdAt, true);
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[var(--charcoal)] px-5 py-4 text-[var(--bone)]" data-testid="image-generating">
      <Loader2 size={18} className="animate-spin" />
      <span className="font-display text-xl">Reimagining this room…</span>
      <span className="ml-auto font-mono-ui text-sm tabular-nums" data-testid="image-generating-elapsed">{fmtElapsed(elapsed)}</span>
    </div>
  );
};

export const FailedVersion = ({ version, onRetry, onOther, busy }) => (
  <div className="rounded-xl border border-[#B42318]/30 bg-[#FDECEA] px-5 py-4" data-testid={version.status === "rejected" ? "image-layout-changed" : "image-failed"}>
    <div className="font-display text-xl">{version.status === "rejected" ? "This render changed the room's layout" : "This vision didn't render."}</div>
    {version.status === "rejected" && version.outputUrl && <img src={assetUrl(version.outputUrl)} alt="Rejected render" className="mt-3 w-full max-w-sm rounded-xl opacity-90" data-testid="image-layout-changed-preview" />}
    {version.status === "rejected" && version.fidelity && <p className="mt-1 text-xs text-[#B42318]/80" data-testid="image-fidelity-note">Structure match {Math.round(version.fidelity.structuralScore * 100)}%{version.fidelity.layoutCheck?.note ? ` · ${version.fidelity.layoutCheck.note}` : ""}</p>}
    {version.status !== "rejected" && version.error && <p className="mt-1 line-clamp-2 text-xs text-[#B42318]/80" data-testid="image-failed-error">{version.error}</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      <button className="btn-primary" onClick={onRetry} disabled={busy} data-testid="image-retry-button"><RotateCcw size={15} /> Try again</button>
      <button className="btn-ghost" onClick={onOther} disabled={busy} data-testid="image-other-engine-button"><Shuffle size={15} /> Try the other engine</button>
    </div>
  </div>
);

export const Controls = ({ current, busy, onRefine, onAlternative, onOther }) => {
  const [text, setText] = useState("");
  return (
    <div className="grid gap-4" data-testid="vision-controls">
      <form className="flex flex-col gap-2 sm:flex-row" onSubmit={(e) => { e.preventDefault(); if (text.trim()) { onRefine(text.trim()); setText(""); } }}>
        <input className="field" value={text} onChange={(e) => setText(e.target.value)} placeholder="Refine — e.g. make the cabinets deep green" data-testid="refine-input" />
        <button className="btn-primary shrink-0" disabled={busy || !text.trim() || !current} data-testid="refine-submit-button"><Sparkles size={15} /> Refine</button>
      </form>
      <div className="flex flex-wrap gap-2">
        <button className="btn-ghost" onClick={onAlternative} disabled={busy} data-testid="alternative-version-button">Alternative version</button>
        <button className="btn-ghost" onClick={onOther} disabled={busy} data-testid="other-engine-button">
          Try the other engine · {ENGINE_LABEL[otherEngine(current?.engine || "flux")]}
        </button>
      </div>
    </div>
  );
};

export const GenerationRecord = ({ v }) => (
  <details className="group rounded-xl border border-[var(--line)] p-4" data-testid="generation-record">
    <summary className="cursor-pointer list-none text-sm font-medium">Generation record · V{v.number} <span className="text-[var(--stone-dark)]">({v.status})</span></summary>
    <dl className="mt-3 grid gap-2 text-xs leading-relaxed text-[var(--ink-soft)]">
      <div><dt className="eyebrow inline">Engine </dt><dd className="inline" data-testid="record-engine">{v.engineLabel} — {v.endpoint}</dd></div>
      {v.engineReason && <div><dt className="eyebrow inline">Why </dt><dd className="inline" data-testid="record-engine-reason">Picked automatically · {v.engineReason}</dd></div>}
      <div><dt className="eyebrow inline">Seed </dt><dd className="inline">{v.seed}</dd></div>
      <div><dt className="eyebrow inline">Grounding </dt><dd className="inline">{v.groundingStatus} · products [{(v.productIds || []).join(", ")}]</dd></div>
      <div><dt className="eyebrow inline">References </dt><dd className="inline break-all">{(v.referenceImageUrls || []).join(", ")}</dd></div>
      {v.durationMs && <div><dt className="eyebrow inline">Time </dt><dd className="inline">{(v.durationMs / 1000).toFixed(1)} s · {v.width}×{v.height}px</dd></div>}
      <div><dt className="eyebrow">Prompt</dt><dd className="mt-1 whitespace-pre-wrap" data-testid="record-prompt">{v.prompt}</dd></div>
    </dl>
  </details>
);
